import { useCallback, useState } from 'react';
import ModuleShell from '../../components/ModuleShell';
import { useForecastScenario } from './useForecastScenario';
import { useScenarioLibrary } from '../opsLibrary/useScenarioLibrary';
import LibraryPanel from '../opsLibrary/LibraryPanel';
import { SCENARIO_LABELS } from './model/scenarios';
import AssumptionsPanel from './components/AssumptionsPanel';
import FunnelPanel from './components/FunnelPanel';
import GrowthPanel from './components/GrowthPanel';
import CohortsPanel from './components/CohortsPanel';
import ChannelsPanel from './components/ChannelsPanel';
import EventsPanel from './components/EventsPanel';
import ScenariosPanel from './components/ScenariosPanel';
import ResultsPanel from './components/ResultsPanel';
import SyncPanel from './components/SyncPanel';
import CalibrateFromActuals from './components/CalibrateFromActuals';
import { mergeScenarioDefaults } from './model/seedDefaults';

const TAB_GROUPS = [
  {
    label: 'Plan setup',
    tabs: [
      { id: 'assumptions', label: 'Assumptions' },
      { id: 'growth', label: 'Growth' },
      { id: 'funnel', label: 'Funnel' },
      { id: 'cohorts', label: 'Cohorts' },
      { id: 'channels', label: 'Channels' },
      { id: 'events', label: 'Events' },
    ],
  },
  {
    label: 'Outcomes',
    tabs: [
      { id: 'results', label: 'Results' },
      { id: 'scenarios', label: 'Scenarios' },
      { id: 'sync', label: 'Sync' },
      { id: 'library', label: 'Library' },
    ],
  },
];

export default function ForecastModule() {
  const [activeTab, setActiveTab] = useState('results');
  const {
    scenario,
    setScenario,
    activePack,
    activeKey,
    patchActivePack,
    setActiveScenario,
    resetPack,
    resetToDefaults,
    projection,
    allProjections,
    pullEconomics,
    pushEconomics,
    syncMessage,
    setSyncMessage,
    exportJson,
    applyLiveCalibration,
    mcPending,
    pushWaitingForMc,
  } = useForecastScenario();

  const applyPayload = useCallback(
    (payload) => {
      setScenario(mergeScenarioDefaults(payload));
    },
    [setScenario]
  );

  const library = useScenarioLibrary('forecast', {
    getPayload: () => scenario,
    applyPayload,
  });

  const handleReset = () => {
    if (window.confirm('Reset all forecast assumptions to defaults?')) {
      resetToDefaults();
      library.clearActiveLink();
    }
  };

  return (
    <ModuleShell
      title="Forecast"
      description={
        <>
          <p>
            SaaS forecast model — phased growth options (fall flat → explode), funnel, cohorts, MRR
            bridge, channels, events, and Monte Carlo bands. Working copy in this browser; use
            Library to save named versions.
          </p>
          <p className="chart-hint">
            Active pack: {SCENARIO_LABELS[activeKey]} · As of {scenario.asOf}
            {library.activeMeta?.libraryName
              ? ` · Saved as “${library.activeMeta.libraryName}”`
              : ' · Illustrative — calibrate to Actuals'}
          </p>
        </>
      }
      actions={
        <>
          <button type="button" className="secondary-button" onClick={exportJson}>
            Export JSON
          </button>
          <button type="button" className="secondary-button" onClick={handleReset}>
            Reset defaults
          </button>
        </>
      }
    >
      <nav className="economics-tabs forecast-tabs forecast-tabs-grouped" aria-label="Forecast sections">
        {TAB_GROUPS.map((group) => (
          <div key={group.label} className="forecast-tab-group">
            <span className="forecast-tab-group-label">{group.label}</span>
            <div className="forecast-tab-group-tabs">
              {group.tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={
                    activeTab === tab.id ? 'economics-tab economics-tab-active' : 'economics-tab'
                  }
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {activeTab === 'assumptions' && (
        <AssumptionsPanel
          assumptions={activePack.assumptions}
          onChange={(assumptions) => {
            const marketing =
              assumptions.marketingPhases?.[0]?.budgetMo ??
              assumptions.marketingBudgetMo ??
              0;
            const mode = activePack.channels?.paid?.budgetMode;
            const linked = mode !== 'custom';
            if (linked) {
              patchActivePack({
                assumptions,
                channels: {
                  ...activePack.channels,
                  paid: {
                    ...activePack.channels.paid,
                    budgetMode: 'link',
                    budgetMo: marketing,
                  },
                },
              });
            } else {
              patchActivePack({ assumptions });
            }
          }}
        />
      )}

      {activeTab === 'growth' && (
        <GrowthPanel
          growth={activePack.growth}
          onChange={(growth) => patchActivePack({ growth })}
        />
      )}

      {activeTab === 'funnel' && (
        <FunnelPanel
          funnel={activePack.funnel}
          onChange={(funnel) => patchActivePack({ funnel })}
        />
      )}

      {activeTab === 'cohorts' && (
        <CohortsPanel
          cohorts={activePack.cohorts}
          onChange={(cohorts) => patchActivePack({ cohorts })}
        />
      )}

      {activeTab === 'channels' && (
        <ChannelsPanel
          channels={activePack.channels}
          marketingBudgetMo={
            activePack.assumptions?.marketingPhases?.[0]?.budgetMo ??
            activePack.assumptions?.marketingBudgetMo
          }
          onChange={(channels) => patchActivePack({ channels })}
        />
      )}

      {activeTab === 'events' && (
        <EventsPanel
          events={activePack.events}
          onChange={(events) => patchActivePack({ events })}
        />
      )}

      {activeTab === 'scenarios' && (
        <ScenariosPanel
          scenario={scenario}
          activeKey={activeKey}
          onSelectScenario={setActiveScenario}
          onResetPack={resetPack}
          onChangeCompareMode={(compareMode) => setScenario((prev) => ({ ...prev, compareMode }))}
          onChangeMonteCarlo={(patch) =>
            setScenario((prev) => ({
              ...prev,
              monteCarlo: { ...prev.monteCarlo, ...patch },
            }))
          }
        />
      )}

      {activeTab === 'results' && (
        <ResultsPanel
          projection={projection}
          allProjections={allProjections}
          compareMode={scenario.compareMode}
          activeKey={activeKey}
          activePack={activePack}
          syncedActuals={scenario.syncedActuals}
          scenarioAsOf={scenario.asOf}
          onPatchPack={patchActivePack}
          mcPending={mcPending}
        />
      )}

      {activeTab === 'sync' && (
        <>
          <SyncPanel
            syncedFromEconomics={scenario.syncedFromEconomics}
            syncedActuals={scenario.syncedActuals}
            syncMessage={syncMessage}
            onPull={pullEconomics}
            onPush={pushEconomics}
            onDismissMessage={() => setSyncMessage(null)}
            pushDisabled={pushWaitingForMc}
          />
          <CalibrateFromActuals onApply={applyLiveCalibration} />
        </>
      )}

      {activeTab === 'library' && (
        <LibraryPanel
          kindLabel="Forecast"
          entries={library.entries}
          activeMeta={library.activeMeta}
          message={library.message}
          onDismissMessage={() => library.setMessage(null)}
          onSaveAs={({ name, notes }) => library.saveAs({ name, notes })}
          onOverwrite={({ id, name, notes }) => library.saveAs({ id, name, notes })}
          onLoad={library.loadEntry}
          onDelete={library.removeEntry}
          onDownloadLibrary={library.downloadLibrary}
          onUploadLibrary={library.uploadLibrary}
        />
      )}
    </ModuleShell>
  );
}
